// The Catches tab: this season's numbers, the fish/game filter and the list of catches by month.
import type { Catch, Kind } from "../shared/model.ts";
import type { TextKey } from "../shared/strings.ts";
import { $, el, icon } from "./dom.ts";
import { compass, fmtMonth, fmtNum, fmtWhen, t } from "./i18n.ts";
import { photoUrl } from "./photos.ts";
import { sizeLine, speciesName } from "./species.ts";
import { catches, onRender, render } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { hasWater } from "./water.ts";
import { openDetail } from "./detail.ts";
import { openEditor } from "./editor.ts";

export type Filter = "all" | Kind;
const savedFilter = lsGet("filter", "all");
let filter: Filter = savedFilter === "fish" || savedFilter === "game" ? savedFilter : "all";

/** The catches the filter lets through, newest first (the list and the map both use this). */
export function filtered(): Catch[] {
  return catches.filter((c) => filter === "all" || c.kind === filter).sort((a, b) => b.time - a.time);
}

function filterChips(target: HTMLElement): void {
  const opts: [Filter, TextKey][] = [["all", "all"], ["fish", "fishPl"], ["game", "gamePl"]];
  target.replaceChildren(...opts.map(([id, key]) => el("button", { class: "chip", type: "button", "aria-pressed": String(filter === id),
    onclick: () => { filter = id; lsSet("filter", id); render(); } }, t(key))));
}

function renderSeason(): void {
  const box = $("#season");
  if (!catches.length) { box.hidden = true; return; }
  const yr = new Date().getFullYear(), list = catches.filter((c) => new Date(c.time).getFullYear() === yr);
  const fish = list.filter((c) => c.kind === "fish"), game = list.filter((c) => c.kind === "game");
  const heavy = list.filter((c) => c.weightKg != null).sort((a, b) => b.weightKg! - a.weightKg!)[0];
  box.replaceChildren(
    el("div", { class: "fish" }, el("b", { text: String(fish.length) }), el("small", { text: t("seasonFish", fish.length, yr) })),
    el("div", { class: "game" }, el("b", { text: String(game.length) }), el("small", { text: t("seasonGame", game.length, yr) })),
    el("div", {}, el("b", { text: heavy ? fmtNum(heavy.weightKg, 1) + " kg" : "–" }), el("small", { text: heavy ? t("heaviestOf", speciesName(heavy)) : t("heaviest") })));
  box.hidden = false;
}

/** Weather in one line under a catch: "12°C · 3 m/s SE · 1013 hPa ↓ · water 14°C". */
function wxChips(c: Catch): HTMLElement | null {
  const w = c.weather;
  if (!c.loc) return null;
  if (!w || w.status !== "ok") return el("div", { class: "wx" }, el("span", { class: "pending", text: navigator.onLine ? t("wxGetting") : t("wxOffline") }));
  const bits: string[] = [];
  if (w.temp != null) bits.push(Math.round(w.temp) + "°C");
  if (w.wind != null) bits.push(fmtNum(w.wind, 0) + " m/s " + (w.windDir != null ? compass(w.windDir) : ""));
  if (w.pressure != null) bits.push(Math.round(w.pressure) + " hPa" + (w.pressure3h != null ? (w.pressure3h <= -1 ? " ↓" : w.pressure3h >= 1 ? " ↑" : " →") : ""));
  if (hasWater(c)) bits.push(t("waterChip", fmtNum(c.water.value, 0)));
  return el("div", { class: "wx" }, bits.map((b) => el("span", { text: b })));
}

function renderList(): void {
  const list = $("#list"), items = filtered();
  if (!items.length) {
    list.replaceChildren(el("div", { class: "empty" }, icon(filter === "game" ? "game" : "fish"),
      el("h2", { text: catches.length ? t("emptyFilteredTitle") : t("emptyTitle") }),
      el("p", { text: catches.length ? t("emptyFiltered", filter) : t("emptyText") }),
      el("button", { class: "btn primary", type: "button", onclick: () => openEditor(null) }, icon("plus"), t("emptyAdd"))));
    return;
  }
  // a heading and a card group for each month
  const out: HTMLElement[] = [];
  let month = "", group = el("div", { class: "cards" });
  for (const c of items) {
    const m = fmtMonth(c.time);
    if (m !== month) { month = m; group = el("div", { class: "cards" }); out.push(el("h2", { class: "month", text: m }), group); }
    const img = el("img", { alt: "" }); img.hidden = true;
    const firstPhoto = c.photoIds?.[0];
    const thumb = el("div", { class: "thumb" }, img, firstPhoto ? null : icon(c.kind), el("span", { class: "kind " + c.kind }));
    if (firstPhoto) void photoUrl(firstPhoto).then((u) => { if (u) { img.src = u; img.hidden = false; } });
    const size = sizeLine(c);
    group.append(el("button", { class: "card", type: "button", onclick: () => openDetail(c.id) }, thumb,
      el("div", { style: "min-width:0" },
        el("h3", { text: speciesName(c) }),
        size ? el("div", { class: "size num", text: size }) : null,
        el("div", { class: "meta num", text: [fmtWhen(c.time), c.place].filter(Boolean).join(" · ") }),
        wxChips(c))));
  }
  list.replaceChildren(...out);
}

onRender(() => {
  filterChips($("#filter"));
  filterChips($("#mapfilter"));
  renderSeason();
  renderList();
});
